type OldConfiguratorStatisticPayload = {
  firmwareVersion: string | null;
  bluetoothId?: string | null;
  serialNumber?: string | null;
};

const ENTRY_ID_STORAGE_KEY = "oldConfiguratorStatisticsEntryId";
const SENT_BY_SERIAL_STORAGE_KEY = "oldConfiguratorStatisticsSentBySerial";
const inFlightStatistics = new Set<string>();

const getStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
};

const generateEntryId = () => {
  const bytes = new Uint8Array(16);

  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] % 16) + 64;
    bytes[8] = (bytes[8] % 64) + 128;

    const hex = Array.from(bytes).map((byte) =>
      byte.toString(16).padStart(2, "0"),
    );

    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
      .slice(6, 8)
      .join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getEntryId = () => {
  const storage = getStorage();

  if (!storage) {
    return generateEntryId();
  }

  const currentEntryId = storage.getItem(ENTRY_ID_STORAGE_KEY);
  if (currentEntryId) {
    return currentEntryId;
  }

  const entryId = generateEntryId();
  storage.setItem(ENTRY_ID_STORAGE_KEY, entryId);
  return entryId;
};

const getToday = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getSentBySerial = (): Record<string, string> => {
  const storage = getStorage();
  const emptyState: Record<string, string> = {};

  if (!storage) {
    return emptyState;
  }

  try {
    const savedState = storage.getItem(SENT_BY_SERIAL_STORAGE_KEY);
    const parsedState = savedState ? JSON.parse(savedState) : emptyState;

    return parsedState &&
      typeof parsedState === "object" &&
      !Array.isArray(parsedState)
      ? parsedState
      : emptyState;
  } catch {
    return emptyState;
  }
};

const markSentForToday = (serialNumber: string, today: string) => {
  const storage = getStorage();

  if (!storage) {
    return;
  }

  storage.setItem(
    SENT_BY_SERIAL_STORAGE_KEY,
    JSON.stringify({
      ...getSentBySerial(),
      [serialNumber]: today,
    }),
  );
};

export const sendOldConfiguratorStatistic = async ({
  firmwareVersion,
  serialNumber,
}: OldConfiguratorStatisticPayload) => {
  const backendApiUrl =
    "https://eu-central-1-staging-backend.aetherdigitaltherapy.com";
  const sanitizedSerialNumber = serialNumber?.trim();

  if (!backendApiUrl || !sanitizedSerialNumber) {
    return;
  }

  const today = getToday();
  const sentBySerial = getSentBySerial();

  if (sentBySerial[sanitizedSerialNumber] === today) {
    return;
  }

  const inFlightKey = `${sanitizedSerialNumber}:${today}`;
  if (inFlightStatistics.has(inFlightKey)) {
    return;
  }

  inFlightStatistics.add(inFlightKey);

  try {
    const response = await fetch(
      `${backendApiUrl.replace(/\/+$/, "")}/api/old-configurator-statistics`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          entry_id: getEntryId(),
          comments: JSON.stringify({
            fw_version: firmwareVersion,
            serial_number: sanitizedSerialNumber,
          }),
        }),
      },
    );

    if (response.ok) {
      markSentForToday(sanitizedSerialNumber, today);
    }
  } catch {
    // Statistic logging should never interrupt the connection flow.
  } finally {
    inFlightStatistics.delete(inFlightKey);
  }
};
